import React, { useState } from 'react';
import { AuditLog, AuditLogAction, FilterAuditLogsParams } from '@/types/audit';
import { fetchAuditLogs } from '@/services/api/audit';
import { FormattedDate } from 'react-intl';
import PaginatedTable from '@/components/ui/paginated';
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { AuditLogDetailsDialog } from './AuditLogDetailsDialog';

interface UserAuditLogsTabProps {
  userId: string;
}

const ALL_ACTIONS = 'ALL_ACTIONS';

const UserAuditLogsTab: React.FC<UserAuditLogsTabProps> = ({ userId }) => {
  const [filters, setFilters] = useState<FilterAuditLogsParams>({
    userId,
    pageNumber: 1,
    pageSize: 10
  });

  const handleFilterChange = (key: keyof FilterAuditLogsParams, value: string | undefined) => {
    setFilters((prev) => ({ ...prev, [key]: value === ALL_ACTIONS ? undefined : value, pageNumber: 1 }));
  };

  return (
    <Card>
      <CardContent>
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div className="space-y-2">
              <Label>Action</Label>
              <Select
                value={filters.action || ALL_ACTIONS}
                onValueChange={(value) => handleFilterChange('action', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All Actions" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL_ACTIONS}>All Actions</SelectItem>
                  {Object.values(AuditLogAction).map((action) => (
                    <SelectItem key={action} value={action}>
                      {action}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Start Date</Label>
              <Input
                type="date"
                value={filters.startDate || ''}
                onChange={(e) => handleFilterChange('startDate', e.target.value || undefined)}
              />
            </div>
            <div className="space-y-2">
              <Label>End Date</Label>
              <Input
                type="date"
                value={filters.endDate || ''}
                onChange={(e) => handleFilterChange('endDate', e.target.value || undefined)}
              />
            </div>
          </div>

          <PaginatedTable
            key={JSON.stringify(filters)}
            source={(page, size) => fetchAuditLogs({ ...filters, pageNumber: page, pageSize: size })}
            renderHeader={() => (
              <TableHeader>
                <TableRow>
                  <TableHead>Action</TableHead>
                  <TableHead>Entity Type</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="w-[100px]"></TableHead>
                </TableRow>
              </TableHeader>
            )}
          >
            {(auditLogs) => (
              <TableBody>
                {auditLogs.map((log) => (
                  <TableRow key={log.id}>
                    <TableCell className="font-medium">{log.action}</TableCell>
                    <TableCell>{log.entityType}</TableCell>
                    <TableCell>
                      <FormattedDate value={log.createdAt} dateStyle="medium" timeStyle="short" />
                    </TableCell>
                    <TableCell>
                      <AuditLogDetailsDialog log={log} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            )}
          </PaginatedTable>
        </div>
      </CardContent>
    </Card>
  );
};

export default UserAuditLogsTab;
